"use client"

import { useEffect, useState, useCallback, useRef } from "react"
import { useRouter } from "next/navigation"
import { pipe } from "fp-ts/lib/function"
import * as E from "fp-ts/Either"
import fetchClientMemberCheck from "./_fetch/fetchClientMemberCheck"
import fetchClientSignUp from "./_fetch/fetchClientSignUp"
import fetchClientRefreshTokens from "./_fetch/fetchClientRefreshTokens"
import fetchClientClearTokens from "./_fetch/fetchClientClearTokens"
import { useUserStore } from "./_store/useUserStore"
import { useFollowingsStore } from "./followings/useFollowingsStore"
import { useFollowersStore } from "./followers/useFollowersStore"
import useMyScrapSentences from "./scrap/useMyScrapSentences"
import useLifeSentence from "./life/useLifeSentence"
import useFolderList from "@/app/components/folder/hooks/useFolderList"
import { nativeBr } from "@/utils/nativeBr"
import useMyBlockUsers from "./userBlock/useMyBlockUsers"
import useContinueWriteList from "@/app/(pages)/sentences/write/_components/continueWrite/hook/useContinueWriteList"
import { useLoadingIndicatorStore } from "@/components/common/loadingIndicator/store/useLoadingIndicatorStore"
import { saveRedirectUrl } from "@/utils/sessionCacheUtils"
import { getCookie } from "@/utils/cookieUtils"
import useKyoboConnection from "./kyobo/useKyoboConnection"
import fetchClientMyProfile from "@/app/(pages)/my/profile/(root)/_fetch/fetchClientMyProfile"

interface UseTokenAuthenticationProps {
  dgtId?: string
  xEncParam?: string
  xEncTime?: string
  redirect?: string
}

const useTokenAuthentication = ({
  dgtId,
  xEncParam,
  xEncTime,
  redirect,
}: UseTokenAuthenticationProps) => {
  const runAnimationLooper = useCallback(
    ({
      task,
      timeoutMs = 500,
    }: {
      task: (elapsedMs: number) => boolean
      timeoutMs?: number
    }) => {
      if (
        typeof window === "undefined" ||
        typeof window.requestAnimationFrame !== "function"
      ) {
        return Promise.resolve(task(timeoutMs))
      }

      return new Promise<boolean>((resolve) => {
        const start = performance.now()

        const loop = (now: number) => {
          const elapsed = now - start
          if (task(elapsed)) {
            resolve(true)
            return
          }

          if (elapsed >= timeoutMs) {
            resolve(false)
            return
          }

          window.requestAnimationFrame(loop)
        }

        window.requestAnimationFrame(loop)
      })
    },
    [],
  )
  const router = useRouter()
  const [isLoading, setIsLoading] = useState(true)
  const [isAgreementRequired, setIsAgreementRequired] = useState(false)
  const isLoggingOut = useRef(false)
  const { hideLoading } = useLoadingIndicatorStore()

  const { fetch: fetchScrapIds, clear: clearScrap } = useMyScrapSentences(false)
  const { fetch: fetchLifeSentence, clearLifeSentence } = useLifeSentence(false)
  const { fetch: fetchFolderList } = useFolderList()
  const { fetch: fetchBlockUsers, clearProfiles: clearBlockUsers } =
    useMyBlockUsers(false)
  const { fetch: fetchContinueWriteList, clear: clearContinueWriteList } =
    useContinueWriteList()

  const { setUser, resetUser } = useUserStore()
  const { clearFollowings } = useFollowingsStore()
  const { clearFollowers } = useFollowersStore()
  const { fetch: fetchKyoboConnection, clear: clearKyoboConnection } =
    useKyoboConnection()

  const clearUserSession = useCallback(
    (hasResetUser: boolean = true) => {
      // 사용자 상태 초기화
      if (hasResetUser) {
        resetUser() // 올바른 사용자 초기화 메서드 사용
      }
      clearFollowings() // following 목록 제거
      clearFollowers() // follower 목록 제거
      clearScrap() // scrap 목록 제거
      clearLifeSentence() // 나의 문장 설정 제거
      clearBlockUsers() // 차단 프로필 목록 제거
      clearContinueWriteList() // 최근 작성문장 목록 제거
      clearKyoboConnection() // 교보문고 연동 상태 조회
    },
    [
      resetUser,
      clearFollowings,
      clearFollowers,
      clearScrap,
      clearLifeSentence,
      clearBlockUsers,
      clearContinueWriteList,
      clearKyoboConnection,
    ],
  )

  const fetchUserData = useCallback(() => {
    // fetchUserInfo()
    fetchScrapIds() // 나의 스크랩한 타인의 문장 목록
    fetchLifeSentence() // 나의 인생문장
    fetchFolderList() // 폴더 리스트
    fetchBlockUsers() // 차단 유저 목록
    fetchContinueWriteList() // 최근 작성 문장
    fetchKyoboConnection() // 교보문고 연동 상태 조회
  }, [
    // fetchUserInfo,
    fetchScrapIds,
    fetchLifeSentence,
    fetchFolderList,
    fetchBlockUsers,
    fetchContinueWriteList,
    fetchKyoboConnection,
  ])

  const handleAuthSuccess = useCallback(
    (
      user: { id: string; nickname: string; imageUrl: string },
      hasRedirect: boolean = true,
    ) => {
      setUser({
        id: user.id,
        name: user.nickname,
        thumbnailUrl: user.imageUrl,
      })
      setIsLoading(false)
      fetchUserData()
      hideLoading()

      if (!hasRedirect) return

      // redirect 처리: 세션스토리지에 저장 후 항상 '/'로 이동
      if (redirect && redirect !== "/") {
        saveRedirectUrl(decodeURIComponent(redirect))
      }
      runAnimationLooper({
        task: () => {
          const hasToken = !!getCookie("rlHasToken")
          if (hasToken) {
            router.replace("/")
            return true
          }
          return false
        },
        timeoutMs: 2000,
      }).then((didFinish) => {
        if (!didFinish) {
          router.replace("/")
        }
      })
    },
    [hideLoading, setUser, fetchUserData, router, redirect, runAnimationLooper],
  )
  const handleTokenAuthentication = useCallback(
    async ({
      dgtId,
      xEncParam,
      xEncTime,
    }: {
      dgtId?: string
      xEncParam?: string
      xEncTime?: string
    }) => {
      try {
        // 인증 시도 전 기존 토큰 삭제
        try {
          // 0.3초 딜레이
          await new Promise((resolve) => setTimeout(resolve, 300))

          const clearResult = await fetchClientClearTokens()
          pipe(
            clearResult,
            E.fold(
              (error) => {
                console.warn("토큰 삭제 실패 (계속 진행):", error.status)
              },
              () => {
                console.log("토큰 삭제 성공")
              },
            ),
          )
        } catch (error) {
          console.warn("토큰 삭제 중 오류 (계속 진행):", error)
        }

        pipe(
          await fetchClientMemberCheck({ dgtId, xEncParam, xEncTime }),
          E.fold(
            (error) => {
              setIsLoading(false)
              //DGT 인증 시간이 만료
              if (error.status === 401) {
                nativeBr.dismiss()
                return
              }
              if (error.status >= 400 && error.status < 500) {
                // 실패시 동의화면 이동
                setIsAgreementRequired(true)
              }
              return
            },
            (response) => {
              // console.log("토큰 조회 성공:", response)
              handleAuthSuccess(response)
            },
          ),
        )
      } catch (error) {
        console.error("catch - Token authentication unexpected error:", error)
      } finally {
        clearUserSession(false)
      }
    },
    [clearUserSession, handleAuthSuccess],
  )

  // sign in 처리
  useEffect(() => {
    if (xEncParam && xEncTime) {
      handleTokenAuthentication({ xEncParam, xEncTime })
    }
    if (dgtId && dgtId.trim() !== "") {
      handleTokenAuthentication({ dgtId: dgtId })
    }
  }, [xEncParam, xEncTime, dgtId]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSignUp = useCallback(
    async ({
      dgtId,
      xEncParam,
      xEncTime,
    }: {
      dgtId?: string
      xEncParam?: string
      xEncTime?: string
    }) => {
      setIsLoading(true)
      try {
        pipe(
          await fetchClientSignUp({ dgtId, xEncParam, xEncTime }),
          E.fold(
            (error) => {
              console.error("Sign up failed:", error)
              console.error(
                "Already registered user or invalid data: ",
                error.message,
              )
              //암호화 시간 만료시 금융앱 이동 처리
              nativeBr.dismiss()
            },
            (response) => {
              handleAuthSuccess(response.member.profile)
            },
          ),
        )
      } catch (error) {
        console.error("Sign up unexpected error:", error)
      } finally {
        clearUserSession(false)
      }
    },
    [clearUserSession, handleAuthSuccess],
  )

  const handleLogout = useCallback(async () => {
    // 중복 로그아웃 방지
    if (isLoggingOut.current) {
      console.warn("로그아웃이 이미 진행 중입니다.")
      return
    }

    isLoggingOut.current = true
    setIsLoading(true)

    try {
      // 토큰 삭제 시도 (실패해도 무시)
      const deleteResult = await fetchClientClearTokens()

      pipe(
        deleteResult,
        E.fold(
          (error) => {
            console.warn("토큰 삭제 실패 (계속 진행):", error.status)
          },
          () => {
            console.log("토큰 삭제 성공")
          },
        ),
      )
    } catch (error) {
      console.warn("토큰 삭제 중 오류 (계속 진행):", error)
    }

    // 항상 세션 정리 및 리다이렉트
    clearUserSession()

    // 네이티브 환경이면 앱 종료, 아니면 로그인 페이지로 이동
    if (nativeBr.isEnabled) {
      nativeBr.dismiss()
    } else {
      setIsLoading(false)
    }

    isLoggingOut.current = false
  }, [clearUserSession])

  const handleRefreshTokens = useCallback(async (force: boolean) => {
    setIsLoading(true)
    try {
      const result = await pipe(
        await fetchClientRefreshTokens(force),
        E.fold(
          (error) => {
            console.error("Token refresh failed:", error)
            // 토큰 갱신 실패 - 상위에서 처리하도록 null 반환
            return null
          },
          (response) => {
            return response
          },
        ),
      )

      return result
    } catch (error) {
      console.error("Token refresh unexpected error:", error)
      return null
    } finally {
      setIsLoading(false)
    }
  }, [])

  const handleUserUpdate = useCallback(async () => {
    // 유저를 초기화 한다.
    clearUserSession()
    return pipe(
      await fetchClientMyProfile(),
      E.fold(
        (error) => {
          console.log("마이그레이션 후 사용자 정보 조회 실패:", error)
          return false
        },
        (updateUser) => {
          handleAuthSuccess(updateUser, false)
          return true
        },
      ),
    )
  }, [clearUserSession, handleAuthSuccess])

  return {
    isLoading,
    isAgreementRequired,
    handleTokenAuthentication,
    handleSignUp,
    handleRefreshTokens,
    handleLogout,
    handleUserUpdate,
  }
}

export default useTokenAuthentication
